import {useTaillightsController} from '@github-ui/taillights/provider'
import type {TaillightsSymbolsHandle} from '@github-ui/taillights-symbols'
import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import type {PreparedView, SourceDocument, SourceRect, SourceSelection} from '@github-ui/taillights-worker/document'
import {lineStarts} from '@github-ui/taillights-worker/source-chunks'
import {
  type RefObject,
  startTransition,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import {anchorFromSelection, rangeFromAnchor, type SourceRange} from './source-navigation'
import {initialNavigationState, sourceNavigationReducer} from './source-navigation-state'

const emptySections: NonNullable<SourceDocument['sections']> = []

function precedingLine(lines: readonly number[], line: number) {
  let left = 0
  let right = lines.length
  while (left < right) {
    const middle = (left + right) >>> 1
    if ((lines[middle] ?? 0) <= line) left = middle + 1
    else right = middle
  }
  return lines[Math.max(0, left - 1)] ?? 0
}

export function useSourceNavigation(
  source: SourceDocument,
  announceStatus: (message: string) => void,
  foldingPreference = true,
  onStickyLineRemoval?: (originalLineNumber: number) => void,
  linesPerChunk = 60,
  scrollOriginRef?: RefObject<HTMLDivElement | null>,
  onSectionCollapse?: (originalLineNumber: number) => void,
  routerHash?: string,
  hashNavigationOffset = 0,
) {
  const controller = useTaillightsController()
  const lines = source.lines
  const sections = source.sections ?? emptySections
  const foldingEnabled = foldingPreference && sections.length > 0
  const [state, dispatch] = useReducer(sourceNavigationReducer, initialNavigationState)
  const {view, intent, anchorRange} = state
  const foldingReady = view !== null && controller !== null && state.preparedFor === controller
  const initialText = useMemo<NativeSelectionMap>(() => {
    const lineNumbers = lines.map((_, index) => index)
    return {
      lines,
      text: lines.join('\n'),
      lineNumbers,
      lineStarts: lineStarts(lines),
      sourceToDisplay: new Map(lineNumbers.map(line => [line, line])),
    }
  }, [lines])
  const textProjection = view ?? initialText
  const symbolsRef = useRef<TaillightsSymbolsHandle>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const documentRef = useRef<HTMLDivElement>(null)
  const committedIntentRef = useRef(-1)
  const appliedHashRef = useRef<string | null>(null)
  const manualHashRef = useRef<string | null>(null)
  const preparedChunksRef = useRef<{view: PreparedView; linesPerChunk: number} | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const [markerSnapshot, setMarkerSnapshot] = useState<{view: typeof view; rects: readonly SourceRect[]}>()
  const visible = view?.collapsed.length ? view.lineNumbers : initialText.lineNumbers
  const collapsed = useMemo(() => new Set(view?.collapsed ?? intent.collapsed), [view, intent.collapsed])
  const profile = state.preparedFor?.profile ?? controller?.profile
  const lineHeight = profile?.lineHeight ?? 20
  const reportFailure = useEffectEvent((error: unknown, failedRevision: number) => {
    if (state.intent.revision === failedRevision) {
      if (!(error instanceof Error && error.name === 'AbortError')) reportError(error)
      announceStatus(`Source preparation failed. ${error instanceof Error ? error.message : String(error)}`)
    }
  })

  const notifyCollapsedSections = useEffectEvent(
    (prepared: PreparedView, revision: number, preparedFor: NonNullable<typeof controller>) => {
      if (!onSectionCollapse || intent.revision !== revision || controller !== preparedFor) return
      for (const start of prepared.collapsed) {
        if (!view || !collapsed.has(start)) onSectionCollapse(start)
      }
    },
  )

  useEffect(() => {
    if (!controller) return
    if (
      intent.cancelled &&
      state.preparedFor === controller &&
      preparedChunksRef.current?.view === state.view &&
      preparedChunksRef.current.linesPerChunk === linesPerChunk
    ) {
      return
    }
    let active = true
    const selection =
      intent.cancelled || state.applied === intent.revision ? symbolsRef.current?.getSelection() : intent.selection
    function failProjection(message: string) {
      startTransition(() => dispatch({type: 'failed', revision: intent.revision, message}))
    }
    async function project() {
      if (!controller) return
      try {
        const prepared = await controller.project({
          collapsed: foldingEnabled ? intent.collapsed : [],
          linesPerChunk,
          selection,
          expandSelection: intent.expandSelection,
        })
        if (!active) return
        preparedChunksRef.current = {view: prepared, linesPerChunk}
        startTransition(() => {
          dispatch({type: 'prepared', revision: intent.revision, view: prepared, controller})
          notifyCollapsedSections(prepared, intent.revision, controller)
        })
      } catch (error) {
        if (!active) return
        const message = error instanceof Error ? error.message : String(error)
        failProjection(message)
        reportFailure(error, intent.revision)
      }
    }
    void project()
    return () => {
      active = false
    }
    // applied is a completion marker, not a new projection request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, intent, foldingEnabled, linesPerChunk])

  const measureOrigin = useCallback(() => {
    // A sticky source plane can move while its document-space origin must stay fixed.
    const element = scrollOriginRef?.current ?? documentRef.current
    if (!element) return
    const top = element.getBoundingClientRect().top + window.scrollY + element.clientTop
    setScrollMargin(previous => (previous === top ? previous : top))
  }, [scrollOriginRef])
  useEffect(() => {
    measureOrigin()
    const observer = new ResizeObserver(measureOrigin)
    let ancestor: HTMLElement | null = scrollOriginRef?.current ?? documentRef.current
    while (ancestor && ancestor !== document.body) {
      observer.observe(ancestor)
      ancestor = ancestor.parentElement
    }
    return () => observer.disconnect()
  }, [measureOrigin, scrollOriginRef])

  const commitNavigation = useEffectEvent(() => {
    if (!view || state.applied !== intent.revision || committedIntentRef.current === state.applied) return
    committedIntentRef.current = state.applied
    const moved =
      intent.selection &&
      view.selection &&
      (intent.selection.start.line !== view.selection.start.line ||
        intent.selection.end.line !== view.selection.end.line)
    if (intent.focus || moved) textareaRef.current?.focus({preventScroll: true})
    const position = intent.position
    if (position) {
      window.scrollTo({top: position.top, behavior: 'instant'})
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Restore a captured offset; this is a write, not a measurement.
      if (viewportRef.current) viewportRef.current.scrollLeft = position.left
    } else if (intent.scroll && view.selection) {
      const line = precedingLine(
        view.lineNumbers,
        moved ? view.selection.start.line : (intent.scrollLine ?? view.selection.start.line),
      )
      const display = view.sourceToDisplay.get(line) ?? 0
      const top = display * lineHeight - (intent.viewportOffset ?? 0)
      window.scrollTo({top: Math.max(0, scrollMargin + top), behavior: 'instant'})
    }
    if (intent.announceFolds) {
      announceStatus(
        `${view.collapsed.length} sections collapsed. ${view.lineNumbers.length} of ${lines.length} source lines visible.${
          moved ? ` Caret moved to visible line ${(view.selection?.start.line ?? 0) + 1}.` : ''
        }`,
      )
    }
    for (const start of intent.collapsed) if (!view.collapsed.includes(start)) onStickyLineRemoval?.(start)
  })
  useEffect(() => {
    commitNavigation()
  }, [view, state.applied])

  useEffect(() => {
    if (!controller || !view) return
    let active = true
    const ranges = view.collapsed
      .filter(line => view.sourceToDisplay.has(line - 1))
      .map(line => {
        const point = {line: line - 1, column: lines[line - 1]?.length ?? 0}
        return {start: point, end: point}
      })
    if (!ranges.length) return
    async function prepareMarkers() {
      if (!controller || !view) return
      try {
        const rects = await controller.rects({ranges, collapsed: view.collapsed})
        if (active) startTransition(() => setMarkerSnapshot({view, rects}))
      } catch (error) {
        if (active) reportFailure(error, state.applied)
      }
    }
    void prepareMarkers()
    return () => {
      active = false
    }
  }, [controller, view, lines, state.applied])

  const setAnchorRange = useCallback(
    (range: SourceRange | null) => {
      // Gutter-owned URL updates must not replace an existing character selection.
      manualHashRef.current = null
      appliedHashRef.current = range ? anchorFromSelection(range, lines) : ''
      dispatch({type: 'anchor', range})
    },
    [lines],
  )
  const cancelPending = useCallback(() => {
    manualHashRef.current = null
    dispatch({type: 'cancel'})
  }, [])
  const onSelectionChange = useCallback(
    (selection: SourceSelection | null) => {
      if (!selection) return
      const scopePending = state.preparedFor !== controller
      if (state.applied !== intent.revision || scopePending) {
        dispatch({type: 'native-selection', selection, scopePending})
      }
    },
    [state.applied, state.preparedFor, controller, intent.revision],
  )
  const revealNativeSelection = useEffectEvent(() => {
    const textarea = textareaRef.current
    const selection = symbolsRef.current?.getSelection()
    if (
      !textarea ||
      !selection ||
      document.activeElement !== textarea ||
      (view && (state.preparedFor !== controller || state.applied !== intent.revision))
    )
      return
    if (
      selection.direction === 'none' &&
      textarea.selectionStart === 0 &&
      textarea.selectionEnd === textProjection.text.length
    )
      return
    // A clipped sticky plane prevents native Find from revealing an offscreen selection.
    const point = selection.direction === 'backward' ? selection.start : selection.end
    const row = textProjection.sourceToDisplay.get(point.line)
    if (row === undefined) return
    const top = scrollMargin + row * lineHeight
    const windowTop = window.scrollY
    const windowHeight = window.innerHeight
    if (top < windowTop || top + lineHeight > windowTop + windowHeight) {
      window.scrollTo({top: Math.max(0, top - windowHeight / 2), behavior: 'instant'})
    }
  })
  useEffect(() => {
    const textarea = textareaRef.current
    if (!scrollOriginRef || !textarea) return
    textarea.addEventListener('selectionchange', revealNativeSelection)
    return () => textarea.removeEventListener('selectionchange', revealNativeSelection)
  }, [scrollOriginRef])

  const navigate = useCallback(
    (range: SourceRange, focus = true, viewportOffset = 0, matchingHash?: string) => {
      manualHashRef.current = matchingHash === appliedHashRef.current ? null : (matchingHash ?? null)
      dispatch({
        type: 'request',
        intent: {
          collapsed: state.applied === intent.revision ? (view?.collapsed ?? intent.collapsed) : intent.collapsed,
          selection: {...range, direction: 'direction' in range ? (range as SourceSelection).direction : 'forward'},
          expandSelection: true,
          focus,
          scroll: true,
          viewportOffset,
        },
      })
    },
    [view, intent.collapsed, intent.revision, state.applied],
  )
  const applyHash = useEffectEvent((hash: string) => {
    if (appliedHashRef.current === hash) return
    appliedHashRef.current = hash
    const range = rangeFromAnchor(hash, lines)
    const preserveManualTarget = manualHashRef.current === hash
    manualHashRef.current = null
    dispatch({type: 'anchor', range})
    // Same-file symbol links scroll first; their matching hash must not replace the column or header offset.
    if (range && !preserveManualTarget) navigate(range, false, hashNavigationOffset)
  })
  useEffect(() => {
    const handleHashChange = () => applyHash(window.location.hash)
    handleHashChange()
    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('statechange', handleHashChange)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('statechange', handleHashChange)
    }
  }, [lines])
  useEffect(() => {
    if (routerHash !== undefined) applyHash(routerHash)
  }, [routerHash])

  const changeFolds = useCallback(
    (requested: ReadonlySet<number>, options?: {preserveScroll: boolean}) => {
      // Clearing folds for a preference change is safe while reconnecting; new folds require current geometry.
      if (!foldingReady && (requested.size || !options?.preserveScroll)) return
      if (!requested.size && !intent.collapsed.length && !view?.collapsed.length) return
      manualHashRef.current = null
      const selection = symbolsRef.current?.getSelection() ?? view?.selection ?? null
      const top = window.scrollY
      const firstDisplay = Math.max(0, Math.floor((top - scrollMargin) / lineHeight))
      const scrollLine = visible[firstDisplay] ?? selection?.start.line ?? 0
      const display = view?.sourceToDisplay.get(scrollLine) ?? 0
      const viewportOffset = display * lineHeight + scrollMargin - top
      for (const start of collapsed) if (!requested.has(start)) onStickyLineRemoval?.(start)
      dispatch({
        type: 'request',
        intent: {
          collapsed: foldingEnabled ? [...requested] : [],
          selection,
          expandSelection: false,
          focus: document.activeElement === textareaRef.current,
          scroll: !options?.preserveScroll,
          scrollLine,
          viewportOffset,
          position: options?.preserveScroll ? {top, left: viewportRef.current?.scrollLeft ?? 0} : undefined,
          announceFolds: true,
        },
      })
    },
    [
      foldingReady,
      view,
      intent.collapsed.length,
      scrollMargin,
      lineHeight,
      visible,
      collapsed,
      onStickyLineRemoval,
      foldingEnabled,
    ],
  )
  const toggleFold = useCallback(
    (startLine: number) => {
      if (!foldingReady) return
      const next = new Set(intent.revision === state.applied ? view?.collapsed : intent.collapsed)
      if (next.has(startLine)) next.delete(startLine)
      else next.add(startLine)
      changeFolds(next)
    },
    [foldingReady, intent, state.applied, view, changeFolds],
  )
  const expandFold = useCallback(
    (startLine: number, focusGutter = false) => {
      if (!foldingReady) return
      if (focusGutter) {
        const gutter = viewportRef.current?.querySelector<HTMLButtonElement>(
          `button[aria-label="Expand section at line ${startLine}"]`,
        )
        ;(gutter ?? textareaRef.current)?.focus({preventScroll: true})
      }
      const next = new Set(state.applied === intent.revision ? view?.collapsed : intent.collapsed)
      next.delete(startLine)
      changeFolds(next, {preserveScroll: true})
    },
    [foldingReady, view, intent.collapsed, intent.revision, state.applied, changeFolds],
  )

  return {
    controller,
    preparedFor: state.preparedFor,
    view,
    textProjection,
    lines,
    sections,
    foldingEnabled,
    foldingReady,
    symbolsRef,
    textareaRef,
    viewportRef,
    documentRef,
    scrollMargin,
    collapsed,
    foldMarkers:
      markerSnapshot?.view === view
        ? markerSnapshot.rects.map(rect => ({
            lineNumber: rect.line + 1,
            left: rect.x + rect.width,
            top: rect.y,
            height: rect.height,
          }))
        : [],
    visible,
    lineHeight,
    status: state.error
      ? 'error'
      : !view
        ? 'plain'
        : !controller || state.preparedFor !== controller || state.applied !== intent.revision
          ? 'preparing'
          : 'prepared',
    error: state.error,
    anchorRange,
    setAnchorRange,
    cancelPending,
    onSelectionChange,
    navigate,
    toggleFold,
    expandFold,
    changeFolds,
  }
}
